import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"
import _imports_0 from '@/assets/icons/arrow-left.svg'


const _hoisted_1 = { class: "flex md:w-176 items-start gap-4 mt-2" }
const _hoisted_2 = { class: "flex md:w-176 items-start gap-4 mt-13 sm:px-1" }
const _hoisted_3 = { class: "mt-6 mb-3" }
const _hoisted_4 = { class: "flex w-[640px] mt-12 items-start content-start gap-4 flex-wrap" }
const _hoisted_5 = { class: "flex items-center space-x-2" }
const _hoisted_6 = { class: "!mt-10 mb-10 md:!mt-12 md:!mb-12" }

import { onMounted, ref } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { useAccountStore, useAnalyticsStore } from '@/store';
import ToggleSwitchCardItem from '@/components/Toggle/ToggleSwitchCardItem.vue';
import { IToggleSwitchCardItem } from '@/interfaces/components/toggle-switch-card.interface';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import {
  CommunicationPreferenceOptions,
  getEnumValue,
  Subscription,
  SubscriptionOptIns,
} from '@/interfaces/self-service/communication-preferences';
import { SelfServiceService } from '@/services/store/self-service-service';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { cloneDeep } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { FormEvents } from '@/enums/form-events.enum';
import { Link } from '@sitecore-jss/sitecore-jss-vue';
import Divider from '@/components/Divider/Divider.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SubscriptionsPage',
  props: {
    content: {},
    id: {},
    name: {}
  },
  setup(__props: any) {

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const props = __props;

const fillTitleBookmark = (content: string) => {
  return replaceBookmark('activeSubscriptions', content, activeSubscriptions.value?.toString());
};

const fillContentBookmark = (content: string) => {
  const emailAddress = accountStore.identityDetails?.email;
  return replaceBookmark('emailAddress', content, emailAddress ?? '');
};

const activeSubscriptionsMessage = ref(getFormInput(props.content, 'activeSubscriptionsMessage'));
const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

/* Subscription options */
const clubNewsMarketing = ref(getFormInput(props.content, 'clubNewsMarketing'));
const roadAheadMagazine = ref(getFormInput(props.content, 'roadAheadMagazine'));
const memberBenefits = ref(getFormInput(props.content, 'memberBenefits'));
const researchAndSurveys = ref(getFormInput(props.content, 'researchAndSurveys'));
const travel = ref(getFormInput(props.content, 'travel'));
const drive = ref(getFormInput(props.content, 'drive'));
const roadTrip = ref(getFormInput(props.content, 'roadTrip'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const subscriptions = ref<IToggleSwitchCardItem[]>([]);
const showSavingOrLoading = ref<boolean>(false);
const hasChanges = ref<boolean>(false);
const activeSubscriptions = ref<number>(0);

const memberSubscriptions = ref<Subscription[]>([]);

const communicationPreferenceOptions = [
  { label: 'Postal mail', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.PostalPhysical] },
  { label: 'Email', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.DigitalEmail] },
];

const model = ref<any>({
  clubNewsMarketingOptIn: false,
  roadAheadMagazineDeliveryOptIn: false,
  deliveryChannelTra: false, //delivery preference for road ahead.
  memberBenefitsOptIn: false,
  researchAndSurveysOptIn: false,
  travelOptIn: false,
  driveOptIn: false,
  roadTripOptIn: false,
});

onMounted(async () => {
  await loadPreferences();
  pushAnalytics();
});

const loadPreferences = async () => {
  showSavingOrLoading.value = true;
  try {
    const d365Id = accountStore.identityDetails?.d365Id ?? '';
    if (d365Id) {
      const response = await SelfServiceService.getSubscriptions(d365Id);

      //Set delivery preference for Road Ahead
      model.value.roadAheadMagazineDeliveryPref = getSelectedValueByValue(response?.deliveryChannelTra);

      if (!model.value.roadAheadMagazineDeliveryPref) {
        model.value.roadAheadMagazineDeliveryPref = 'Email';
      }

      memberSubscriptions.value = response?.subscriptions;
      loadContent();
    }
  } catch (error) {
    console.error(error);
  }

  showSavingOrLoading.value = false;
};

const loadContent = () => {
  model.value.clubNewsMarketingOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.marketingOptIntype
  )?.isOptIn;

  model.value.roadAheadMagazineDeliveryOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.roadAheadOptInType
  )?.isOptIn;

  model.value.memberBenefitsOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.memberBenefitsOptInType
  )?.isOptIn;

  model.value.researchAndSurveysOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.researchAndSurveysOptInType
  )?.isOptIn;

  model.value.travelOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.travelOptInType
  )?.isOptIn;

  model.value.driveOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.driveOptInType
  )?.isOptIn;

  model.value.roadTripOptIn = memberSubscriptions.value.find(
    (x: any) => x?.type?.id === SubscriptionOptIns.roadTripOptInType
  )?.isOptIn;

  subscriptions.value = [];

  subscriptions.value.push({
    id: SubscriptionOptIns.marketingOptIntype,
    title: clubNewsMarketing.value?.title?.value,
    description: clubNewsMarketing.value?.subTitle?.value,
    modelValue: model.value.clubNewsMarketingOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.roadAheadOptInType,
    title: roadAheadMagazine.value?.title?.value,
    description: roadAheadMagazine.value?.subTitle?.value,
    modelValue: model.value.roadAheadMagazineDeliveryOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.memberBenefitsOptInType,
    title: memberBenefits.value?.title?.value,
    description: memberBenefits.value?.subTitle?.value,
    modelValue: model.value.memberBenefitsOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.researchAndSurveysOptInType,
    title: researchAndSurveys.value?.title?.value,
    description: researchAndSurveys.value?.subTitle?.value,
    modelValue: model.value.researchAndSurveysOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.travelOptInType,
    title: travel.value?.title?.value,
    description: travel.value?.subTitle?.value,
    modelValue: model.value.travelOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.driveOptInType,
    title: drive.value?.title?.value,
    description: drive.value?.subTitle?.value,
    modelValue: model.value.driveOptIn,
  });

  subscriptions.value.push({
    id: SubscriptionOptIns.roadTripOptInType,
    title: roadTrip.value?.title?.value,
    description: roadTrip.value?.subTitle?.value,
    modelValue: model.value.roadTripOptIn,
  });

  countActiveSubscriptions();
};

const countActiveSubscriptions = () => {
  activeSubscriptions.value = 0;
  if (model.value.clubNewsMarketingOptIn) activeSubscriptions.value = 1;
  if (model.value.roadAheadMagazineDeliveryOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
  if (model.value.memberBenefitsOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
  if (model.value.researchAndSurveysOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
  if (model.value.travelOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
  if (model.value.driveOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
  if (model.value.roadTripOptIn) activeSubscriptions.value = activeSubscriptions.value + 1;
};

const onOptInChange = async (selection: any) => {
  await onSaveOptInChange(selection);
};

const onOptionChange = async (e: any) => {
  //Set road ahead delivery preference
  const commPref = getSelectedValueByDisplayValue(e);
  model.value.deliveryChannelTra = commPref;

  await onSaveOptInChange(
    { id: SubscriptionOptIns.roadAheadOptInType, value: model.value.roadAheadMagazineDeliveryOptIn },
    commPref
  );
  hasChanges.value = true;
};

const onSaveOptInChange = async (selection: any, deliveryPref: string = '') => {
  const d365Id = accountStore.identityDetails?.d365Id ?? '';
  if (d365Id) {
    //Road ahead has been opted in so we default the delivery preference to Email.
    if (!deliveryPref && selection.id === SubscriptionOptIns.roadAheadOptInType && selection.value) {
      deliveryPref = getSelectedValueByDisplayValue('Email') ?? '';
    }

    const updateSubscription = cloneDeep(memberSubscriptions.value.find((x) => x.type?.id == selection.id));

    if (updateSubscription) {
      updateSubscription.isOptIn = selection.value as boolean;

      if (deliveryPref) {
        updateSubscription.deliveryChannelTra = deliveryPref;
      }

      try {
        const response = await SelfServiceService.updateSubscriptionOptIn(d365Id, updateSubscription);

        if (response?.subscriptions?.length ?? 0 > 0) {
          toastAlertStore.addToastAlert({
            title: 'Changes saved',
            description: '',
            type: ToastAlertType.SUCCESS,
            duration: 6000,
          });

          memberSubscriptions.value = response?.subscriptions;
          loadContent();

          try {
            await analyticsStore.pushAnalytics({
              event: FormEvents.COMPLETE,
              form: {
                name: 'Newsletter and subscriptions',
                stepName: 'FormCompleted',
              },
              profileInfo: {
                subscriptionsArray: buildAnalyticsSubscriptionEntry(),
              },
            });
          } catch (error) {
            console.error('Failed to push analytics after subscription update', error);
          }
          return;
        }
      } catch (error) {
        console.error(error);

        toastAlertStore.addToastAlert({
          title: 'Unable to save changes',
          description: '',
          type: ToastAlertType.ERROR,
          duration: 6000,
        });

        await analyticsStore.pushAnalytics({
          event: FormEvents.JEOPARDY,
          form: {
            name: 'Newsletter and subscriptions',
            formErrorType: 'Unable to save changes',
            formErrorDetail: 'Unable to save changes',
          },
        });
      }
    }
  }
};

const getOptionsForInput = () => {
  return communicationPreferenceOptions.map((opt) => {
    return opt.label;
  });
};

const getSelectedValueByDisplayValue = (selectedOption: string) => {
  const selected = communicationPreferenceOptions.find((x) => x.label.toLowerCase() === selectedOption?.toLowerCase());
  return selected?.value;
};

const getSelectedValueByValue = (value: string) => {
  const enumValue = getEnumValue(value);
  if (enumValue !== undefined) {
    const selected = communicationPreferenceOptions.find((x) => x.value === CommunicationPreferenceOptions[enumValue]);
    return selected?.label;
  }
  return '';
};

const buildAnalyticsSubscriptionEntry = (): Record<string, string>[] | undefined => {
  let subscriptionsArray: Record<string, string>[] = [];

  for (const subscription of subscriptions.value) {
    if (subscription.id === SubscriptionOptIns.roadAheadOptInType) {
      subscriptionsArray.push({
        title: subscription.title || '',
        isOptIn: subscription.modelValue ? `On - ${model.value.roadAheadMagazineDeliveryPref}` : 'Off',
      });
    } else {
      subscriptionsArray.push({
        title: subscription.title || '',
        isOptIn: subscription.modelValue ? 'On' : 'Off',
      });
    }
  }

  return subscriptionsArray;
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);

  if (!pageAnalytics.pageAnalytics.profileInfo?.subscriptionsArray) {
    pageAnalytics.pageAnalytics.profileInfo!.subscriptionsArray = [];
  }

  pageAnalytics.pageAnalytics.profileInfo!.subscriptionsArray = buildAnalyticsSubscriptionEntry();

  pageAnalytics.pushAnalytics();
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(FormStepInputs, {
      step: {
      stepName: _ctx.name,
      formInputs: _ctx.content,
    }
    }, {
      activeSubscriptionsMessage: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(InformationBox, {
            title: fillTitleBookmark(activeSubscriptionsMessage.value.title.value),
            description: fillContentBookmark(activeSubscriptionsMessage.value.content.value),
            type: activeSubscriptions.value > 0 ? _unref(InformationBoxTypes).SUCCESS : _unref(InformationBoxTypes).INFO,
            isCircle: true,
            useButtonSlot: false
          }, null, 8, ["title", "description", "type"])
        ])
      ]),
      clubNewsMarketing: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(SectionPanel, null, {
            buttonAction: _withCtx(() => [
              (_ctx.$slots.buttonAction)
                ? _renderSlot(_ctx.$slots, "buttonAction", { key: 0 })
                : _createCommentVNode("", true)
            ]),
            sectionPanelContent: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subscriptions.value, (item) => {
                return (_openBlock(), _createBlock(ToggleSwitchCardItem, {
                  id: item.id,
                  key: item.id,
                  title: item.title,
                  description: item.description,
                  modelValue: item.modelValue,
                  disabled: item.disabled,
                  noPanel: true,
                  vertAlignment: true,
                  onChange: _cache[2] || (_cache[2] = (selection) => onOptInChange(selection))
                }, _createSlots({ _: 2 }, [
                  (item.id == _unref(SubscriptionOptIns).roadAheadOptInType && model.value.roadAheadMagazineDeliveryOptIn)
                    ? {
                        name: "otheractions",
                        fn: _withCtx(() => [
                          _createElementVNode("div", _hoisted_3, [
                            _createVNode(_component_FormKit, {
                              modelValue: model.value.roadAheadMagazineDeliveryPref,
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((model.value.roadAheadMagazineDeliveryPref) = $event)),
                              label: _unref(getFormInput)(props.content, 'roadAheadMagazineDeliveryPref')?.label?.value,
                              innerClass: 'text-base',
                              inputClass: "text-lg",
                              type: "nativeRadio",
                              name: "deliveryChannelNotices",
                              outerClass: "-mt-1",
                              options: getOptionsForInput(),
                              onInput: _cache[1] || (_cache[1] = (e) => onOptionChange(e))
                            }, null, 8, ["modelValue", "label", "options"])
                          ])
                        ]),
                        key: "0"
                      }
                    : undefined
                ]), 1032, ["id", "title", "description", "modelValue", "disabled"]))
              }), 128))
            ]),
            _: 3
          })
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(Link), {
            field: discardChangesLink.value?.link,
            class: "btn btn-tertiary btn-md"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _cache[3] || (_cache[3] = _createElementVNode("img", {
                  src: _imports_0,
                  alt: "left arrow",
                  class: "w-5 h-5 inline-block"
                }, null, -1)),
                _createElementVNode("span", null, _toDisplayString(discardChangesLink.value?.link?.value?.text), 1)
              ])
            ]),
            _: 1
          }, 8, ["field"])
        ])
      ]),
      _: 3
    }, 8, ["step"]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(Divider)
    ]),
    (showSavingOrLoading.value)
      ? (_openBlock(), _createBlock(FullScreenSpinner, {
          key: 0,
          applyOpacity: true
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})